/**
 * Bearer-token storage for the Capacitor app.
 *
 * Holds the active access token in memory for the API client and mirrors
 * writes to `capacitor-secure-storage-plugin` (Keychain on iOS, EncryptedSharedPreferences
 * on Android, localStorage on web). The persisted copy lets the app survive
 * force-quit + restart without forcing the user to re-sign-in — `rehydrateAuthToken`
 * is called at startup before the first session query fires.
 *
 * The backend (`apps/api`) currently uses better-auth's bearer plugin and
 * returns a single long-lived access token from `auth/sign-in` — no refresh
 * token is issued. When that lands, this module is the right place for the
 * refresh handshake (see `apps/app/src/lib/api-client.ts`'s `onError` 401 hook).
 */

import { getItem, removeItem, setItem } from '@/lib/secure-storage';
import { markSessionAuthenticated } from '@/lib/session-lifecycle';
import { Capacitor } from '@capacitor/core';

const ACCESS_TOKEN_KEY = 'borradh.auth.accessToken';

let inMemoryToken: string | undefined;
let rehydrated = false;

const listeners = new Set<(token: string | undefined) => void>();

export function getAuthToken(): string | undefined {
  return inMemoryToken;
}

/**
 * May the bearer token be PERSISTED on this platform?
 *
 * Native: yes — Keychain / EncryptedSharedPreferences, and there is no cookie
 * jar to fall back on. Local dev: yes — Vite on localhost calling a tunnelled
 * API is cross-site, so the SameSite=Lax session cookie is never sent.
 *
 * Production web: NO. `secure-storage`'s web fallback is `localStorage` with
 * base64 (encoding, not encryption), so persisting here left a 7-day bearer
 * readable by any XSS on app.borradh.io — defeating the HttpOnly cookie that
 * already authenticates that origin (ENG-577 H4).
 *
 * The in-memory token is unaffected: it still serves the current page, so
 * nothing about this turn breaks. What is lost on production web is surviving
 * a reload on the TOKEN — and the cookie survives that instead.
 */
const canPersistToken = (): boolean =>
  Capacitor.isNativePlatform() || import.meta.env.DEV;

export function setAuthToken(token: string | undefined): void {
  inMemoryToken = token;
  if (token) {
    markSessionAuthenticated();
    if (!canPersistToken()) {
      for (const listener of listeners) listener(token);
      return;
    }
    void setItem(ACCESS_TOKEN_KEY, token).catch(() => {
      // Secure storage unavailable (e.g., uninitialised on web) — in-memory
      // token still serves the current session.
    });
  } else {
    void removeItem(ACCESS_TOKEN_KEY).catch(() => {});
  }
  for (const listener of listeners) {
    listener(token);
  }
}

/**
 * Install a token and resolve only once the persisted copy is written.
 *
 * `setAuthToken` fires the storage write and returns immediately, which is
 * fine when the caller stays on the page. Callers that hard-navigate straight
 * afterwards (impersonate / stop-impersonating) must await this instead: a
 * reload that beats the write would rehydrate the PREVIOUS token and undo the
 * identity swap.
 */
export async function setAuthTokenAndPersist(token: string): Promise<void> {
  inMemoryToken = token;
  markSessionAuthenticated();
  try {
    if (canPersistToken()) {
      await setItem(ACCESS_TOKEN_KEY, token);
    }
  } catch {
    // Secure storage unavailable — the in-memory token still serves this page.
  }
  for (const listener of listeners) {
    listener(token);
  }
}

export function clearAuthToken(): void {
  setAuthToken(undefined);
}

export function onAuthTokenChange(
  listener: (token: string | undefined) => void
): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Restore the persisted access token into memory. Called once at app startup
 * before the first session query — without it a cold-launch fires `auth/session`
 * with no bearer and the API responds 401, forcing the user back to sign-in.
 *
 * Gated the same way `setAuthToken`/`setAuthTokenAndPersist` gate their
 * writes — `canPersistToken()` — because a `localStorage` entry written
 * before that write-side gate existed (ENG-577 H4) survives indefinitely on
 * production web otherwise: this read had no gate of its own, so it kept
 * rehydrating that leftover token into memory on every page load for any
 * account that had signed in on web before the fix shipped — silently, until
 * some other code path unconditionally reads `getAuthToken()` and sends it.
 */
export async function rehydrateAuthToken(): Promise<void> {
  if (rehydrated) return;
  rehydrated = true;
  if (!canPersistToken()) {
    // Purge rather than merely skip: a pre-ENG-577 write may still be
    // sitting here, and leaving it in storage keeps it one future
    // unconditional `getAuthToken()` call away from being sent again.
    void removeItem(ACCESS_TOKEN_KEY).catch(() => {});
    return;
  }
  const stored = await getItem(ACCESS_TOKEN_KEY);
  if (stored) {
    inMemoryToken = stored;
    for (const listener of listeners) {
      listener(stored);
    }
  }
}
